import { createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../indexedDB';
import { registerOfflineAction } from '../../helpers/actionRegistry';
import { fetchCompanyUsersRequest } from '../slices/database/companyUsersSlice';
import {
	getDatabaseRecord,
	getAllDatabaseRecord,
	patchDatabase,
	postDatabaseRecord,
	getTrucksData,
	getAuthzPublicMenu,
	getAuthzCompanies,
	getCompanyRoles,
	addUser,
	unassignUser,
	addTransporterGenerator,
	editTransporterGenerator,
	deleteTransporterGenerator,
	getCRDetails,
	addTransporterGeneratorContact,
	editTransporterGeneratorContact,
	deleteTransporterGeneratorContact,
	addTransporterTruck,
	editTransporterTruck,
	deleteTransporterTruck,
	addTemplate,
	deleteTemplate,
	deleteTemplateVersion,
	getTemplateUsage,
	setTemplateVersionInUse,
	downloadTemplate,
	getCompanyUsers,
	getAssignableRoles,
	addCompanyUser,
	editCompanyUser,
	updateCompanyUserRoles,
	removeCompanyUser,
	getTransporterGeneratorsData,
	getCompanyBranches,
	getTransporterGeneratorContactsData,
	getDomainData,
	getServicesData,
	getUserManualsData,
	getImportDataTypeConfigs,
	importCustomData,
	deleteDatabaseRecord
} from "api/databaseApi";
import { logger } from "helpers/logger";
import { downloadBlob } from "helpers/downloadUtils";

// #region DATABASE
export const fetchDatabaseRecord = createAsyncThunk("mds/fetchDatabaseRecord", async ({ tableName, fieldName, itemId, columns }, { rejectWithValue, signal }) => {
	try {
		const response = await getDatabaseRecord({ tableName, fieldName, itemId, columns }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch database record:", error);
		return rejectWithValue(error.message || "Failed to fetch database record");
	}
});

export const fetchDatabaseRecords = createAsyncThunk("mds/fetchDatabaseRecords", async ({ tableName, condition, columns }, { rejectWithValue, signal }) => {
	try {
		const response = await getAllDatabaseRecord({ tableName, condition, columns }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch database records:", error);
		return rejectWithValue(error.message || "Failed to fetch database records");
	}
});

export const patchDatabaseRecord = createAsyncThunk("mds/patchDatabaseRecord", async (params, { rejectWithValue, signal }) => {
	try {
		const { JsonToPatch } = params;

		// Pass both if HeaderParameters exists
		const response = await patchDatabase(JsonToPatch, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to patch database record:", error);
		return rejectWithValue(error.message || "Failed to patch database record");
	}
});

export const postDatabaseRecordThunk = createAsyncThunk("mds/postDatabaseRecordThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { jsonBody } = params;

		const response = await postDatabaseRecord(jsonBody, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to post database record:", error);
		return rejectWithValue(error.message || "Failed to post database record");
	}
});

export const deleteDatabaseRecordThunk = createAsyncThunk("mds/deleteDatabaseRecordThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const jsonBody = params?.jsonBody || params?.JsonToDelete || params;

		const response = await deleteDatabaseRecord(jsonBody, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to delete database record:", error);
		return rejectWithValue(error.message || "Failed to delete database record");
	}
});

// #endregion

// #region COMPANIES

export const fetchCompanyUsersThunk = createAsyncThunk("mds/fetchCompanyUsersThunk", async (params, { rejectWithValue }) => {
	try {
		const { companyId } = params;

		const response = await getCompanyUsers(companyId);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch company users:", error);
		return rejectWithValue(error.message || "Failed to fetch company users");
	}
});

// #endregion

// #region ROLES

export const fetchCompanyRolesThunk = createAsyncThunk(
	'mds/fetchCompanyRolesThunk',
	async (params, { rejectWithValue, signal }) => {
		try {
			const { companyId } = params;

			const response = await getCompanyRoles(companyId, signal);
			return response;
		} catch (error) {
			if (error.name === 'AbortError') {
				logger.info("🚫 Request cancelled");
				return rejectWithValue("cancelled");
			}
			logger.error("❌ Failed to fetch company roles:", error);
			return rejectWithValue(error.message || "Failed to fetch company roles");
		}
	}
);

// #endregion

// #region PUBLIC

export const fetchAuthzPublicMenuThunk = createAsyncThunk("mds/fetchAuthzPublicMenuThunk", async ({ domainId }, { rejectWithValue, signal }) => {
	try {
		const response = await getAuthzPublicMenu(domainId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get authz public menu:", error);
		return rejectWithValue(error.message || "Failed to get authz public menu");
	}
});

// authz.* schema: fetches the caller's companies on demand (not tied to fetchUserCompaniesRequest's
// saga) - used by Layout's handleSwitchCompany, which needs to await the actual response inline to
// decide whether to navigate or warn, rather than fire-and-forget a saga-driven request.
export const fetchUserCompaniesThunk = createAsyncThunk("mds/fetchUserCompaniesThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const response = await getAuthzCompanies(signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get user companies:", error);
		return rejectWithValue(error.message || "Failed to get user companies");
	}
});

export const fetchDomainDataThunk = createAsyncThunk("mds/fetchDomainDataThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { domainName } = params;
		const response = await getDomainData(domainName, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get domain data:", error);
		return rejectWithValue(error.message || "Failed to get domain data");
	}
});

export const fetchServicesDataThunk = createAsyncThunk("mds/fetchServicesDataThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { domainId, pageName } = params;
		const response = await getServicesData(domainId, pageName, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get services data:", error);
		return rejectWithValue(error.message || "Failed to get services data");
	}
});

export const fetchUserManualsDataThunk = createAsyncThunk("mds/fetchUserManualsDataThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const response = await getUserManualsData(signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			console.log("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		return rejectWithValue(error.message || "Failed to get user manuals data");
	}
});

export const fetchImportDataTypesThunk = createAsyncThunk("mds/fetchImportDataTypesThunk", async ({ domainId }, { rejectWithValue, signal }) => {
	try {
		const response = await getImportDataTypeConfigs(domainId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch import data types:", error);
		return rejectWithValue(error.message || "Failed to fetch import data types");
	}
});
// #endregion

// #region USERS

export const addUserThunk = createAsyncThunk("mds/addUserThunk", async (params, { rejectWithValue, dispatch }) => {
	const { companyId, keycloakCompanyId, body } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'addUserThunk',
			payload: { companyId, keycloakCompanyId, body },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await addUser(companyId, keycloakCompanyId, body);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to add user");
	}
});

// The backend's message for a failed request; for a validation error (422) its list of problems
// ("Mobile Number is required; ID Number: digits only"), which says more than the summary.
const apiErrorMessage = (error, fallback) =>
	Array.isArray(error?.apiErrors) && error.apiErrors.length > 0 ? error.apiErrors.join("; ") : error?.message || fallback;

// Add user on the authz tables (Manage Users). Offline it is queued and replayed later; the backend
// then runs every check (form config, manage_users, assignable roles) as for a live request.
export const addCompanyUserThunk = createAsyncThunk("mds/addCompanyUserThunk", async (params, { rejectWithValue, dispatch }) => {
	const { companyId, body } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'addCompanyUserThunk',
			payload: { companyId, body },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await addCompanyUser(companyId, body);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(apiErrorMessage(error, "Failed to add user"));
	}
});

// Edit user on the authz tables (Manage Users): only the changed fields. Queued offline like Add; the
// backend runs every check when it is replayed.
// reloadList = false: don't reload the Manage Users list afterwards (My Profile - the user may not hold
// manage_users, which the list needs).
export const editCompanyUserThunk = createAsyncThunk("mds/editCompanyUserThunk", async (params, { rejectWithValue, dispatch }) => {
	const { companyId, userId, body, reloadList = true } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'editCompanyUserThunk',
			payload: { companyId, userId, body },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await editCompanyUser(companyId, userId, body);
		if (companyId && reloadList) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(apiErrorMessage(error, "Failed to edit user"));
	}
});

// Modify roles on the authz tables (Manage Users): sends the member's complete new set of roles. Queued
// offline like the others; sending the same set again changes nothing, so a replay is safe.
export const updateCompanyUserRolesThunk = createAsyncThunk("mds/updateCompanyUserRolesThunk", async (params, { rejectWithValue, dispatch }) => {
	const { companyId, userId, body } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'updateCompanyUserRolesThunk',
			payload: { companyId, userId, body },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await updateCompanyUserRoles(companyId, userId, body);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(apiErrorMessage(error, "Failed to update the roles"));
	}
});

// Remove a member from the company (Manage Users) - their membership there ends, nothing else. Queued
// offline like the others; the backend runs every check when it is replayed.
export const removeCompanyUserThunk = createAsyncThunk("mds/removeCompanyUserThunk", async (params, { rejectWithValue, dispatch }) => {
	const { companyId, userId } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'removeCompanyUserThunk',
			payload: { companyId, userId },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await removeCompanyUser(companyId, userId);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(apiErrorMessage(error, "Failed to remove the user"));
	}
});

// The roles the caller may assign in a company (the Add user / Edit roles picker).
export const fetchAssignableRolesThunk = createAsyncThunk("mds/fetchAssignableRolesThunk", async ({ companyId }, { rejectWithValue, signal }) => {
	try {
		return await getAssignableRoles(companyId, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch assignable roles:", error);
		return rejectWithValue(apiErrorMessage(error, "Failed to load the roles"));
	}
});


export const unassignUserThunk = createAsyncThunk("mds/unassignUserThunk", async (params, { rejectWithValue, dispatch }) => {
	const { userId, companyId, keycloakCompanyId } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'unassignUserThunk',
			payload: { userId, companyId, keycloakCompanyId },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await unassignUser(userId, companyId, keycloakCompanyId);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to unassign user");
	}
});

// ─── Offline Action Registry ──────────────────────────────────────────────────
// Register every thunk that has been upgraded with the offline-aware pattern
// (navigator.onLine check + db.action_queue + follow-up dispatch inside thunk).
//
// Pattern to follow when upgrading a new thunk:
//   1. Add `if (!navigator.onLine) { await db.action_queue.add({...}); return { queued: true }; }`
//   2. Move any follow-up dispatch (e.g. fetchCompanyUsersRequest) INTO the thunk on success
//   3. Add `registerOfflineAction('thunkName', thunkName)` below
//
// ✅ Upgraded thunks:
registerOfflineAction('addUserThunk', addUserThunk);
registerOfflineAction('unassignUserThunk', unassignUserThunk);
registerOfflineAction('addCompanyUserThunk', addCompanyUserThunk);
registerOfflineAction('editCompanyUserThunk', editCompanyUserThunk);
registerOfflineAction('updateCompanyUserRolesThunk', updateCompanyUserRolesThunk);
registerOfflineAction('removeCompanyUserThunk', removeCompanyUserThunk);

// #endregion


// #region GENERATORS

export const fetchTransporterGeneratorThunk = createAsyncThunk("mds/fetchTransporterGeneratorThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getTransporterGeneratorsData(companyId, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get Transporter Generators.");
	}
});

export const addTransporterGeneratorThunk = createAsyncThunk("mds/addTransporterGeneratorThunk", async (params, { rejectWithValue }) => {
	try {
		const { companyId, data } = params;

		const response = await addTransporterGenerator(companyId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Transporter Generator.");
	}
});

export const editTransporterGeneratorThunk = createAsyncThunk("mds/editTransporterGeneratorThunk", async (params, { rejectWithValue }) => {
	try {
		const { generatorId, data } = params;

		const response = await editTransporterGenerator(generatorId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Edit Transporter Generator.");
	}
});

export const deleteTransporterGeneratorThunk = createAsyncThunk("mds/deleteTransporterGeneratorThunk", async (params, { rejectWithValue }) => {
	try {
		const { generatorId } = params;

		const response = await deleteTransporterGenerator(generatorId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Transporter Generator.");
	}
});

// #endregion

// #region CONTACTS

export const getCompanyContactsThunk = createAsyncThunk("mds/getCompanyContactsThunk", async (params, { dispatch, rejectWithValue, signal }) => {
	try {
		const { companyId } = params;
		const condition = `companyId=eq.${companyId}`;

		const result = await dispatch(fetchDatabaseRecords({ tableName: "tbl_contacts", condition }));

		if (fetchDatabaseRecords.fulfilled.match(result)) {
			return result.payload;
		} else {
			return rejectWithValue(result.payload || "Failed to fetch company contacts.");
		}
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch company contacts:", error);
		return rejectWithValue(error.message || "Failed to fetch company contacts.");
	}
});

// #endregion

// #region CONTACTS/DELEGATES
export const fetchTransporterGeneratorContactsThunk = createAsyncThunk("mds/fetchTransporterGeneratorContactsThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getTransporterGeneratorContactsData(companyId, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get Transporter Generator Contact.");
	}
});

export const addTransporterGeneratorContactThunk = createAsyncThunk("mds/addTransporterGeneratorContactThunk", async (params, { rejectWithValue }) => {
	try {
		const { companyId, data } = params;

		const response = await addTransporterGeneratorContact(companyId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Transporter Generator Contact.");
	}
});

export const editTransporterGeneratorContactThunk = createAsyncThunk("mds/editTransporterGeneratorContactThunk", async (params, { rejectWithValue }) => {
	try {
		const { contactId, data } = params;

		const response = await editTransporterGeneratorContact(contactId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Edit Transporter Generator Contact.");
	}
});

export const deleteTransporterGeneratorContactThunk = createAsyncThunk("mds/deleteTransporterGeneratorContactThunk", async (params, { rejectWithValue }) => {
	try {
		const { contactId } = params;

		const response = await deleteTransporterGeneratorContact(contactId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Transporter Generator Contact.");
	}
});

// #endregion

// #region TRUCKS

export const fetchTrucksThunk = createAsyncThunk("mds/fetchTrucksThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getTrucksData(companyId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch company trucks:", error);
		return rejectWithValue(error.message || "Failed to fetch company trucks");
	}
});

export const addTransporterTruckThunk = createAsyncThunk("mds/addTransporterTruckThunk", async (params, { rejectWithValue }) => {
	try {
		const { relatedCompanyId, data } = params;

		const response = await addTransporterTruck(relatedCompanyId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Transporter Truck.");
	}
});

export const editTransporterTruckThunk = createAsyncThunk("mds/editTransporterTruckThunk", async (params, { rejectWithValue }) => {
	try {
		const { truckId, data } = params;

		const response = await editTransporterTruck(truckId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Edit Transporter Truck.");
	}
});

export const deleteTransporterTruckThunk = createAsyncThunk("mds/deleteTransporterTruckThunk", async (params, { rejectWithValue }) => {
	try {
		const { truckId } = params;

		const response = await deleteTransporterTruck(truckId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Transporter Truck.");
	}
});

// #endregion

// #region MOC
export const getCRDetailsThunk = createAsyncThunk("mds/getCRDetailsThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { unn } = params;

		const response = await getCRDetails(unn, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get UNN Details.");
	}
});
// #endregion

// #region TEMPLATES

export const addTemplateThunk = createAsyncThunk("mds/addTemplateThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateName, file } = params;

		const response = await addTemplate(templateName, file);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Template.");
	}
});

// Whole template (all versions) that the version templateId belongs to.
export const deleteTemplateThunk = createAsyncThunk("mds/deleteTemplateThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateId } = params;

		const response = await deleteTemplate(templateId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Template.");
	}
});

// One version (not the one in use).
export const deleteTemplateVersionThunk = createAsyncThunk("mds/deleteTemplateVersionThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateId } = params;

		const response = await deleteTemplateVersion(templateId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Template Version.");
	}
});

// What deleting a version / its template would affect (for the Delete dialog).
export const getTemplateUsageThunk = createAsyncThunk("mds/getTemplateUsageThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateId } = params;

		const response = await getTemplateUsage(templateId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get Template Usage.");
	}
});

// Makes a version the one in use.
export const setTemplateVersionInUseThunk = createAsyncThunk("mds/setTemplateVersionInUseThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateId } = params;

		const response = await setTemplateVersionInUse(templateId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Use Template Version.");
	}
});

export const downloadTemplateThunk = createAsyncThunk("mds/downloadTemplateThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateId, fallbackFileName } = params;

		// The file is saved here, so the (non-serializable) blob never goes through the store.
		const { blob, fileName } = await downloadTemplate(templateId);
		const savedAs = fileName || fallbackFileName;
		downloadBlob(blob, savedAs);
		return { fileName: savedAs };
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Download Template.");
	}
});
// #endregion

// #region BRANCHES

export const fetchCompanyBranchesThunk = createAsyncThunk("mds/fetchCompanyBranchesThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getCompanyBranches(companyId, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get Company Branches.");
	}
});

// #endregion



// #region IMPORT DATA
export const importCustomDataThunk = createAsyncThunk("mds/importCustomDataThunk", async (params, { rejectWithValue }) => {
	try {
		const { tableKey, bodyData } = params;
		const response = await importCustomData(tableKey, bodyData);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Import Custom Data.");
	}
});
// #endregion